/**
 * MONSTER TOWN — BODY SHAPE TABLE. MONSTER CREW (src/monster/**).
 *
 * PURE DATA. Every proportion of every monster in the game is one row here: how
 * fat the torso is, how long the legs are, where the face sits, how deep the
 * head sinks into the body, where a hat lands.
 *
 * ADDING A BODY SHAPE IS ONE ENTRY. `art.ts` never needs to change — the same
 * data-not-engine rule the placeable registry runs on.
 *
 * All numbers are in RIG-LOCAL units: the rig is authored at interior scale and
 * the caller scales the whole thing up outdoors.
 */

import type { MonsterBodyShape } from '../core/types'

// ---------------------------------------------------------------------------
// shape table — DATA. A new body shape is one entry here.
// ---------------------------------------------------------------------------

export interface ShapeSpec {
  /** true = ellipsoid torso, false = chunky box torso. */
  readonly roundTorso: boolean
  readonly torsoW: number
  readonly torsoH: number
  readonly torsoD: number
  readonly legH: number
  readonly legW: number
  readonly legGap: number
  readonly armY: number
  readonly armLen: number
  readonly armR: number
  /** Head pivot height, relative to the hips (= body origin). */
  readonly headY: number
  /**
   * How far FORWARD the head sits. Not decoration: under a fixed 34-degree
   * camera a deep box torso rises in front of whatever is behind it, and a head
   * sunk into the middle of one has its mouth and chin cut off by the torso's
   * own front-top edge. Round-bodied shapes need none of this and leave it at 0.
   */
  readonly headZ: number
  /** null = no separate head; the face sits straight on the torso. */
  readonly headShape: { w: number; h: number; d: number; round: boolean; y: number } | null
  /**
   * Face HEIGHT relative to the head pivot. There is deliberately no `faceZ`:
   * how far forward a feature sits is READ OFF the body it is drawn on
   * (`art.ts` projects every eye, mouth and blush onto the actual surface), so
   * a face can never float in front of a monster or sink inside one.
   */
  readonly faceY: number
  readonly eyeSpread: number
  readonly eyeR: number
  /** Hat attach height relative to the head pivot. */
  readonly hatY: number
  /** Reference width a hat is sized against. */
  readonly headW: number
  readonly height: number
  /**
   * WHERE THE PATTERN LIVES on the torso, as a fraction of `torsoH`: the centre
   * of the band, and how much of the torso it may use.
   *
   * This exists because on `blob` and `round` the FACE IS ON THE TORSO — there
   * is no separate head — so a belly patch sized for a body ran straight up
   * into the mouth and read as a huge white chin. Headless shapes get a low
   * tummy under the face; headed ones sit their band near the middle.
   *
   * `patternScale` is NOT a raw size any more, it is how much of the torso the
   * VISIBLE patch may span. `1` used to mean "the whole front face", which on a
   * flat-fronted body printed the accent across it like a label on a crate.
   */
  readonly patternCY: number
  readonly patternScale: number
}

export const SHAPES: Record<MonsterBodyShape, ShapeSpec> = {
  blob: {
    roundTorso: true,
    torsoW: 0.86,
    torsoH: 0.92,
    torsoD: 0.8,
    legH: 0.16,
    legW: 0.22,
    // Legs tucked IN under a round body: the further out they sit, the more of
    // the plain column shows below the belly instead of just the feet.
    legGap: 0.18,
    // Arms hang from the LOWER half. On a headless shape the face is on the
    // torso, so arms parked at the widest point sat either side of the eyes and
    // read as ears.
    armY: 0.33,
    armLen: 0.32,
    armR: 0.1,
    headY: 0.46,
    headZ: 0,
    headShape: null,
    faceY: 0.23,
    eyeSpread: 0.18,
    eyeR: 0.11,
    hatY: 0.47,
    headW: 0.66,
    height: 1.08,
    // Face is on the torso: the tummy sits LOW, well under the mouth.
    patternCY: 0.17,
    patternScale: 0.52,
  },
  tall: {
    /**
     * ROUND, NOT A BOX — and the head is NARROWER than the body.
     *
     * This shape shipped as a 0.50 x 0.62 x 0.46 box carrying a 0.56-wide head:
     * wider on top than underneath, so the two pieces continued each other into
     * one straight-sided column, and a box under a 34-degree key light shades
     * its two visible faces to nearly the same value. The critic read the result
     * exactly — "a flat rectangular slab with a straw hat, a postbox with a
     * face" — standing next to correctly-rounded neighbours in the same frame.
     *
     * A creature reads as a creature when its silhouette CURVES and its head is
     * a separate, smaller ball sunk into a wider body. Nothing else about the
     * shape changes: it is still the narrow, long-legged one.
     */
    roundTorso: true,
    torsoW: 0.6,
    torsoH: 0.66,
    torsoD: 0.54,
    legH: 0.4,
    legW: 0.17,
    legGap: 0.2,
    armY: 0.43,
    armLen: 0.36,
    armR: 0.1,
    // The head SINKS INTO the torso. Two volumes that merely touch read as a
    // stack of crates, not a creature — the outlines meet in a hard seam.
    headY: 0.58,
    headZ: 0.02,
    headShape: { w: 0.54, h: 0.52, d: 0.5, round: true, y: 0.24 },
    faceY: 0.28,
    eyeSpread: 0.14,
    eyeR: 0.095,
    hatY: 0.48,
    headW: 0.54,
    height: 1.49,
    patternCY: 0.44,
    patternScale: 0.58,
  },
  round: {
    roundTorso: true,
    torsoW: 1.0,
    torsoH: 0.9,
    torsoD: 0.94,
    legH: 0.1,
    legW: 0.24,
    legGap: 0.21,
    armY: 0.32,
    armLen: 0.3,
    armR: 0.11,
    headY: 0.45,
    headZ: 0,
    headShape: null,
    faceY: 0.24,
    eyeSpread: 0.21,
    eyeR: 0.125,
    hatY: 0.47,
    headW: 0.72,
    height: 1.0,
    patternCY: 0.17,
    patternScale: 0.5,
  },
  chunky: {
    /**
     * The same box-torso problem `tall` had, and it was in every crowd too:
     * `shots/critic-r3/c6-street.png` shows this shape as a hard-edged green
     * crate with a head on it. STOCKY is a proportion, not a primitive — this is
     * now the wide, barrel-bodied one, still the only shape with a big round
     * head on a short-legged body, and it no longer flat-shades to a slab.
     */
    roundTorso: true,
    torsoW: 0.82,
    torsoH: 0.6,
    torsoD: 0.66,
    legH: 0.24,
    legW: 0.24,
    legGap: 0.28,
    armY: 0.36,
    armLen: 0.3,
    armR: 0.11,
    // Wide body, ROUND head, sunk in — and the head is smaller than the body it
    // stands on, so the silhouette necks in instead of running straight up.
    headY: 0.52,
    headZ: 0.02,
    headShape: { w: 0.64, h: 0.58, d: 0.6, round: true, y: 0.24 },
    faceY: 0.3,
    eyeSpread: 0.17,
    eyeR: 0.105,
    hatY: 0.5,
    headW: 0.64,
    height: 1.32,
    patternCY: 0.42,
    patternScale: 0.56,
  },
}

export function shapeHeight(shape: MonsterBodyShape): number {
  return (SHAPES[shape] ?? SHAPES.blob).height
}
